import {
  createContext,
  FunctionComponent,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import FrameHubspotHoc from '@components/instrumentation/hubspot';
import {
  ContextualSaveBar,
  ContextualSaveBarProps,
  Layout,
  Page,
  PageActions,
  PageActionsProps,
} from '@shopify/polaris';
import { Container } from '@ui/container/common';
import Modal, { ModalRef } from '@ui/modal';
import { Toast } from '@ui';
import PendingSubscriptionModal from '@components/modals/pending-subscription';
import {
  useFetchAccount,
  useInitializeAmplitude,
  useInitializeFullStory,
  useInitializeHubspot,
  useRedirectToImpersonatePage,
} from '@hooks';
import { useAuthContext, useCurrentSession } from '@hooks/auth';
import { useRouter } from 'next/router';
import { Routes } from '@routes';
import { useFetchGivzFeatures } from '@hooks/feature';

const ChildrenContainer = styled.div<{
  hasGutter: boolean;
}>`
  margin-bottom: ${(props) => (props.hasGutter ? '6rem' : undefined)};

  @media (max-width: 768px) {
    margin-bottom: ${(props) => (props.hasGutter ? '9rem' : undefined)};
  }
`;

const FooterContainer = styled(Container)`
  margin-top: 2rem;
  margin-bottom: 6rem;

  @media (max-width: 1400px) {
    div {
      justify-content: flex-start;
    }
  }

  @media (max-width: 768px) {
    margin-bottom: 8rem;
  }
`;

type AppProps = {
  showSaveBar: boolean;
  showPageActions: boolean;
};

type AppContextProps = {
  setShowSave?(status: boolean): void;
  updateSaveBar?(props: ContextualSaveBarProps): void;
  setShowPageActions?(status: boolean): void;
  updatePageActions?(props: PageActionsProps): void;
  getModalRef?(): any;
} & AppProps;

export const AppContext = createContext<AppContextProps>({
  showPageActions: false,
  showSaveBar: false,
});

export const AppProvider: FunctionComponent<AppProps> = ({
  children,
  showPageActions,
  showSaveBar,
}) => {
  useFetchAccount();
  useFetchGivzFeatures();
  useInitializeAmplitude();
  useInitializeFullStory();
  useInitializeHubspot();
  useRedirectToImpersonatePage();
  const modalRef = useRef<ModalRef>();
  const [showSave, setShowSave] = useState(showSaveBar);
  const [showActions, setShowPageActions] = useState(showPageActions);
  const [saveBarProps, updateSaveBar] = useState<ContextualSaveBarProps>({
    fullWidth: true,
  });
  const [pageActions, updatePageActions] = useState<PageActionsProps>();
  const authContext = useAuthContext();
  const currentShopifySession = useCurrentSession();
  const [errorMessage] = useState<Error | undefined>();
  const router = useRouter();

  function onModalRef(ref: ModalRef) {
    modalRef.current = ref;
  }

  useEffect(() => {
    if (!authContext && !errorMessage) {
      throw new Error('Access token missing from context');
    }

    if (!currentShopifySession && !errorMessage) {
      throw new Error('Shopify session does not exist');
    }
  }, [authContext, currentShopifySession, errorMessage]);

  return (
    <AppContext.Provider
      value={{
        getModalRef: () => modalRef.current,
        setShowPageActions,
        setShowSave,
        showPageActions: showActions,
        showSaveBar: showSave,
        updatePageActions,
        updateSaveBar,
      }}
    >
      <FrameHubspotHoc>
        <Page>
          {router.asPath !== Routes.account.subscription &&
            router.asPath !== Routes.overview && <PendingSubscriptionModal />}
          {showSave && <ContextualSaveBar {...saveBarProps} />}
          <Toast />
          <ChildrenContainer hasGutter={!showActions}>
            {children}
          </ChildrenContainer>
          {showActions && (
            <FooterContainer>
              <Layout>
                <Layout.Section>
                  <PageActions {...pageActions} />
                </Layout.Section>
              </Layout>
            </FooterContainer>
          )}
          <Modal onRef={onModalRef} />
        </Page>
      </FrameHubspotHoc>
    </AppContext.Provider>
  );
};

export const useApp = (): AppContextProps => useContext(AppContext);
